'use client'

import Button from '@/app/components/ui/buttons/button'
import { useCart } from '@/hooks/use-cart'
import { logger } from '@/lib/logger'
import { cFormatter } from '@/lib/utils'
import { useCartTotals } from '@/stores/cart-store'
import { ShoppingCart } from 'lucide-react'
import { useRouter } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'

export const Cart = () => {
  const { items, cart, refetch } = useCart()
  const totals = useCartTotals()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { push } = useRouter()
  const wrapperRef = useRef<HTMLButtonElement>(null)

  // Use cart items if available, otherwise fall back to store items
  const cartItems = cart?.items || items

  const navToCart = () => {
    // TODO: Replace with Next.js route when cart page is migrated
    // For now, using legacy HTML page
    const url = '/pages/public/checkout-cart.html'

    // Extract school/org ID from cart items for URL params
    const firstItem = cartItems?.[0]
    const schoolId = firstItem?.schoolId

    // Build URL with appropriate query params
    const params = new URLSearchParams()
    if (schoolId) {
      params.append('s', schoolId.toString())
    }

    const finalUrl = params.toString() ? `${url}?${params.toString()}` : url
    push(finalUrl)
  }
  useEffect(() => {
    logger.debug('Cart component items', { items: cartItems })
  }, [cartItems])
  useEffect(() => {
    logger.debug('Cart component totals', { totals })
  }, [totals])
  // Don't render cart icon if no cart key exists (user hasn't started shopping)
  if (!cart && !cartItems?.length) return null
  return (
    <div className="relative">
      <button
        type="button"
        ref={wrapperRef}
        className="relative flex-shrink-0 rounded-full p-1 text-surfacePrimary hover:text-surfacePrimary-darker"
        data-testid="cart-icon"
        onMouseEnter={() => {
          setIsMenuOpen(true)
          // Refetch cart data when hovering to ensure it's up to date
          refetch()
        }}
      >
        <span className="sr-only">View Cart</span>
        <div className="relative inline-block">
          <ShoppingCart className="h-8 w-8" onClick={() => {}} />
          {/* Cart badge - only show when items exist */}
          {cartItems && cartItems.length > 0 && (
            <div className="absolute -top-3 left-4 text-white">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-danger">
                <span className="text-xs font-medium text-white" data-testid="cart-badge-count">
                  {cartItems.length}
                </span>
              </div>
            </div>
          )}
        </div>
      </button>
      {isMenuOpen && (
        <div
          className="absolute -right-2 z-10 mt-2 w-64 origin-top-right rounded-md bg-white p-2 text-center text-black shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          data-testid="cart-dropdown"
          onMouseLeave={() => setIsMenuOpen(false)}
          tabIndex={-1}
        >
          <h4 className="uppercase text-black">Items added to cart</h4>
          <div className="mt-4 flex flex-row justify-between">
            <span>Items</span>
            <span data-testid="cart-dropdown-count">{cartItems?.length || 0}</span>
          </div>
          <div className="flex flex-row justify-between">
            <span>Subtotal</span>
            <span className="font-semibold" data-testid="cart-dropdown-subtotal">
              {cFormatter(totals.subtotal)}
            </span>
          </div>
          <Button variant="primary" onClick={navToCart} data-testid="view-cart-button">
            View Cart
          </Button>
        </div>
      )}
    </div>
  )
}
